import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Check, X } from "lucide-react";

const STATUS_COLOR = {
  Pending: { bg: "#D97706", label: "Pending" },
  Approved: { bg: "#059669", label: "Approved" },
  Rejected: { bg: "#DC2626", label: "Rejected" },
};

export default function Leaves() {
  const { user } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ from_date: "", to_date: "", reason: "", type: "Personal" });

  const load = async () => {
    const { data } = await api.get("/leaves"); setLeaves(data);
  };
  useEffect(() => { load(); }, []);

  const apply = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leaves", form);
      toast.success("Leave applied");
      setOpen(false); setForm({ from_date: "", to_date: "", reason: "", type: "Personal" });
      load();
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail)); }
  };

  const action = async (id, status) => {
    try {
      await api.post(`/leaves/${id}/action`, { status });
      toast.success(`Leave ${status.toLowerCase()}`);
      load();
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="leaves-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#64748B] mb-1">Leave Management</div>
          <h1 className="font-heading text-3xl font-bold text-[#0F172A]">{user.role !== "student" ? "All Leave Requests" : "My Leave Requests"}</h1>
        </div>
        {user.role === "student" && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="apply-leave-button" className="bg-[#0F2E4A] hover:bg-[#174169] text-white">
                <Plus className="w-4 h-4 mr-1.5" /> Apply Leave
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Apply for Leave</DialogTitle></DialogHeader>
              <form onSubmit={apply} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold text-[#475569]">From Date *</Label>
                    <Input data-testid="leave-from" type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} required className="mt-1.5" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-[#475569]">To Date *</Label>
                    <Input data-testid="leave-to" type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} required className="mt-1.5" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#475569]">Leave Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="leave-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Personal">Personal</SelectItem>
                      <SelectItem value="Sick">Sick</SelectItem>
                      <SelectItem value="Emergency">Emergency</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#475569]">Reason *</Label>
                  <Textarea data-testid="leave-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} required className="mt-1.5" />
                </div>
                <DialogFooter>
                  <Button type="submit" className="bg-[#0F2E4A] hover:bg-[#174169] text-white" data-testid="submit-leave">Submit Application</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC] text-left text-[11px] font-semibold uppercase tracking-wider text-[#475569]">
            <tr>
              {user.role !== "student" && <th className="px-4 py-3">Student</th>}
              <th className="px-4 py-3">From</th>
              <th className="px-4 py-3">To</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Reason</th>
              <th className="px-4 py-3">Applied</th>
              <th className="px-4 py-3">Status</th>
              {user.role !== "student" && <th className="px-4 py-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody data-testid="leaves-list">
            {leaves.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-[#64748B]">No leave requests.</td></tr>}
            {leaves.map((l) => (
              <tr key={l.id} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]">
                {user.role !== "student" && <td className="px-4 py-3 font-medium text-[#0F172A]">{l.student_name}</td>}
                <td className="px-4 py-3">{l.from_date}</td>
                <td className="px-4 py-3">{l.to_date}</td>
                <td className="px-4 py-3">{l.type}</td>
                <td className="px-4 py-3 max-w-xs truncate text-[#475569]">{l.reason}</td>
                <td className="px-4 py-3 text-[#64748B]">{new Date(l.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">
                  <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded" style={{ backgroundColor: `${STATUS_COLOR[l.status].bg}15`, color: STATUS_COLOR[l.status].bg }}>
                    {l.status}
                  </span>
                </td>
                {user.role !== "student" && (
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {l.status === "Pending" && (
                      <>
                        <button onClick={() => action(l.id, "Approved")} data-testid={`approve-${l.id}`} className="p-2 hover:bg-[#ECFDF5] rounded">
                          <Check className="w-4 h-4 text-[#059669]" />
                        </button>
                        <button onClick={() => action(l.id, "Rejected")} data-testid={`reject-${l.id}`} className="p-2 hover:bg-[#FEF2F2] rounded">
                          <X className="w-4 h-4 text-[#DC2626]" />
                        </button>
                      </>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
